import { useDispatch } from "react-redux";
import {
  addNewOrder,
  setOrderHistory,
  setOrdersList,
} from "../features/orderStatusHistories/ordersStatusHistoriesSlice";
import { AppDispatch } from "../redux/store";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetch3, fetch4, fetch5, fetch8 } from "../commonApis/commonApis";
import { extractErrorMessage } from "../utilities/apiErrorHandler";

const url = process.env.REACT_APP_BASE_URL;

interface ApiResponse {
  success: boolean;
  data: any;
  message?: string;
  statusCode?: number;
  statusMessage?: string;
  error?: any;
}

// ─── Order Create ─────────────────────────────────────────────────────────────

export const useOrderCreateMutation = () => {
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (payload: any) => {
      // fetch4 now throws automatically if success:false — no manual check needed
      const res: ApiResponse = await fetch4(`${url}/api/orders`, payload);
      dispatch(addNewOrder(res?.data));
      return res.data;
    },
  });
};

export const useFetchOrderLineItemsWithStock = (orderId: string) => {
  return useQuery({
    queryKey: ["orderLineItemsStock", orderId],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/orders/${orderId}/line-items-stock`, "get");
      if (!res.success) throw new Error(res.message || "Failed to fetch line items");
      return res.data as Array<{
        id: string;
        sku: string | null;
        productId: string | null;
        name: string | null;
        quantity: number;
        price: number;
        weight?: number;
        availableStock: number;
        hasStock: boolean;
        isSplit: boolean;
        product?: { id: string; itemName: string; sku: string; image?: string };
      }>;
    },
    enabled: !!orderId,
    staleTime: 0, // always fresh — stock changes frequently
  });
};
 
export const useSplitOrderMutation = (orderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { selectedItemIds: string[] }) => {
      const res = await fetch4(`${url}/api/orders/${orderId}/split`, payload);
      if (!res.success) throw new Error(res.message || "Failed to split order");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orderLineItemsStock", orderId] });
    },
  });
};

// ─── Orders List ──────────────────────────────────────────────────────────────

export const useFetchOrdersData = (
  page: number,
  limit: number,
  searchFor: string,
  reverse?: boolean,
  pending?: boolean,
  cancelled?: boolean,
  deleted?: boolean,
  filters?: any
) => {
  const dispatch: AppDispatch = useDispatch();
  return useQuery({
    queryKey: [
      "ordersData",
      page,
      limit,
      searchFor,
      reverse,
      pending,
      cancelled,
      deleted,
      filters,
    ],
    queryFn: async () => {
      const filtersParam = encodeURIComponent(JSON.stringify(filters || []));
      const res = await fetch3(
        `${url}/api/orders?page=${page}&limit=${limit}&searchFor=${searchFor}&reverse=${reverse}&pending=${pending}&cancelled=${cancelled}&deleted=${deleted}&filter=${filtersParam}`,
        "get"
      );
      dispatch(setOrdersList(res.data));
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

// ─── Service List ─────────────────────────────────────────────────────────────

export const useServiceAllListData = () => {
  return useQuery({
    queryKey: ["serviceAllList"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/service/all`, "get");
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

// ─── Route Orders ─────────────────────────────────────────────────────────────

export const useFetchRouteOrdersData = (
  page: number,
  searchFor: string,
  routeCode: string,
  requestType: string,
  drsId?: string,
  warehouseId?: string
) => {
  return useQuery({
    queryKey: ["routeOrdersData", routeCode, page, searchFor, requestType, drsId, warehouseId],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/orders/by-route/${routeCode}?page=${page}&searchFor=${searchFor}&requestType=${requestType}&drsId=${drsId}&warehouseId=${warehouseId ?? ""}`,
        "get"
      );
      return res.data;
    },
    enabled: !!routeCode || !!warehouseId || !!drsId,
    staleTime: 1000 * 60 * 5,
  });
};

// ─── Deleted Orders ───────────────────────────────────────────────────────────

export const useFetchDeleteOrdersData = (page: number, searchFor: string) => {
  return useQuery({
    queryKey: ["deleteOrdersData"],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/orders/deleted?page=${page}&searchFor=${searchFor}`,
        "get"
      );
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

// ─── Order Details ────────────────────────────────────────────────────────────

export const useFetchOrderDetailsData = (awbNumber?: any) => {
  return useQuery({
    queryKey: ["OrderDetails", awbNumber],
    queryFn: async () => {
      if (!awbNumber) return null;
      const res = await fetch3(`${url}/api/orders/${awbNumber}?type=LM`, "get");
      return res.data;
    },
    enabled: !!awbNumber,
    staleTime: 0,
  });
};

// ─── Order Status History ─────────────────────────────────────────────────────

export const useFetchOrderDetailsStatusHistoryData = (awbNumber?: string) => {
  const dispatch: AppDispatch = useDispatch();
  return useQuery({
    queryKey: ["OrderDetailsStatusHistory", awbNumber],
    queryFn: async () => {
      if (!awbNumber) return null;
      const res = await fetch3(
        `${url}/api/orders/${awbNumber}/status-histories?type=LM`,
        "get"
      );
      const orderId = res.data[0]?.orderId;
      if (orderId) {
        dispatch(setOrderHistory({ orderId, history: res.data }));
      }
      return res.data;
    },
    enabled: !!awbNumber,
    staleTime: 0,
  });
};

// ─── Order Delete ─────────────────────────────────────────────────────────────

export const useOrderDeleteMutation = (id: string) => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(`${url}/api/orders/${id}`, "DELETE", payload);
      return res.data;
    },
  });
};

// ─── Order Sync ───────────────────────────────────────────────────────────────


export const useOrderRouteSyncMutation = () => {
  return useMutation({
    mutationFn: async () => {
      const res: ApiResponse = await fetch5(`${url}/api/orders/sync-routes`, "PATCH");
      return res;
    },
  });
};

export const useOrderSyncMutation = () => {
  return useMutation({
    mutationFn: async () => {
      const res: ApiResponse = await fetch5(`${url}/api/orders/sync-order`, "PATCH");
      return res;
    },
  });
};

// ─── Order Revert ─────────────────────────────────────────────────────────────

export const useOrderRevertMutation = (id: string) => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(`${url}/api/orders/${id}/revert`, "PATCH", payload);
      return res.data;
    },
  });
};

// ─── Order Update ─────────────────────────────────────────────────────────────

export const useOrderUpdateMutation = (id: string) => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(`${url}/api/orders/${id}`, "PATCH", payload);
      return res.data;
    },
  });
};

// ─── Order Label Print (PDF) ─────────────────────────────────────────────────

export const useOrderLabelPrintMutation = (id: string) => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const response = await fetch(`${url}/api/orders/${id}/generate/pdf`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        mode: "cors",
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          extractErrorMessage(errorData, "Failed to generate PDF")
        );
      }

      return response.blob();
    },
    onSuccess: (pdfBlob: Blob) => {
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, "_blank");
    },
  });
};

// ─── Reverse Order ────────────────────────────────────────────────────────────

export const useOrderReverseMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch4(`${url}/api/orders/reverse-orders`, payload);
      return res.data;
    },
  });
};

// ─── Order Status Revert ──────────────────────────────────────────────────────

export const useOrderStatusRevertMutation = (orderId: string) => {
  return useMutation({
    mutationFn: async () => {
      const res: ApiResponse = await fetch5(
        `${url}/api/orders/${orderId}/update-status-order`,
        "PATCH"
      );
      return res.data;
    },
  });
};

// ─── Order Status Revert (Managed) ───────────────────────────────────────────

export const useOrderStatusRevertStatusMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/orders/revert/manage`,
        "PATCH",
        payload
      );
      return res;
    },
  });
};

// ─── Order Status Update (Managed) ───────────────────────────────────────────

export const useOrderStatusUpdateStatusMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/orders/update/manage`,
        "PATCH",
        payload
      );
      return res;
    },
  });
};

// ─── Bulk Order Upload ────────────────────────────────────────────────────────

export const useOrderBulkDocMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch8(`${url}/api/bulk/bulk-orders`, "POST", payload);
      return res;
    },
  });
};

// ─── Bulk Order Forward ───────────────────────────────────────────────────────

export const useOrderForwardBulkMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(`${url}/api/bulk/bulk-forward`, "POST", payload);
      return res;
    },
  });
};

// ─── Order Template ───────────────────────────────────────────────────────────

export const useFetchOrderTemplate = () => {
  return useQuery({
    queryKey: ["templates"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/templates/orders`, "get");
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

// ─── Driver Locations Map ─────────────────────────────────────────────────────

export const useFetchDriversMapData = () => {
  return useQuery({
    queryKey: ["driversMapData"],
    queryFn: async () => {
      try {
        const res = await fetch4(`${url}/api/driver/current-locations`, {});
        return res.data || [];
      } catch {
        return [];
      }
    },
    staleTime: 1000 * 10,
    refetchInterval: 10000,
  });
};

// ─── FM Bulk Order Upload ─────────────────────────────────────────────────────

export const useFmOrderBulkDocMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch8(`${url}/api/bulk/bulk-fm-orders`, "POST", payload);
      if (!res.success) {
        throw res.message || "Something went wrong";
      }
      return res;
    },
    onSuccess: (res: any) => {
      console.log("✅ FM Bulk Orders uploaded successfully:", res);
      return res;
    },
    onError: (error: any) => {
      console.error("❌ FM Bulk Orders upload failed:", error.message);
    },
  });
};

// ─── FM Order Template ─────────────────────────────────────────────────────────

export const useFetchFmOrderTemplate = () => {
  return useQuery({
    queryKey: ["fmTemplates"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/templates/fm-orders`, "get");
      if (!res || !res.data) return res.message;
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};
